// public/scripts/my-games-client.js
// Import the Firebase Functions client SDK
import { getFunctions, httpsCallable } from "https://www.gstatic.com/firebasejs/10.12.2/firebase-functions.js";
import { getAuth } from "https://www.gstatic.com/firebasejs/10.12.2/firebase-auth.js";

// Reference to the callable function (initialized later)
let getMyGamesFunction;
// let joinGameFunction; // REMOVED - Handled by join-game-modal.js
// Add reference for delete game function
let deleteGameFunction;

// --- NEW: Helper function to get first name ---
function getFirstName(displayName) {
    if (!displayName || typeof displayName !== 'string') {
        return "Player"; // Fallback
    }
    const nameParts = displayName.trim().split(/\s+/);
    return nameParts[0] || "Player"; // Return first part or fallback
}
// --- END NEW Helper ---

// Function to handle opening a game
function handleOpenGame(gameId) {
    if (!gameId) return;
    console.log(`handleOpenGame: Navigating to game ${gameId}...`);
    window.location.href = `/krida.html?gameId=${gameId}`;
}

// --- handleJoinGame function REMOVED - Now in join-game-modal.js ---

// Function to handle deleting a game
async function handleDeleteGame(gameId) {
    if (!gameId) return;
    
    if (!confirm("Are you sure you want to delete this game? This action cannot be undone.")) {
        return;
    }
    
    console.log(`handleDeleteGame: Attempting to delete game ${gameId}...`);

    if (!deleteGameFunction) {
        try {
            const functions = getFunctions();
            deleteGameFunction = httpsCallable(functions, 'deleteGame');
        } catch (error) {
            console.error("handleDeleteGame: Error getting Firebase Functions instance:", error);
            alert("Error initializing delete game service.");
            return;
        }
    }

    const button = document.querySelector(`.delete-game-btn[data-game-id="${gameId}"]`);
    if (button) button.disabled = true;

    try {
        const result = await deleteGameFunction({ gameId: gameId });
        if (result.data?.success) {
            console.log(`handleDeleteGame: Successfully deleted game ${gameId}`);
            const listItem = button?.closest('li');
            if (listItem) {
                listItem.remove();
                 // Check if the list is now empty
                const listElement = listItem.parentElement;
                if (listElement && listElement.children.length === 0) {
                    const activeGamesListDiv = document.getElementById('active-games-list');
                    if (activeGamesListDiv) activeGamesListDiv.innerHTML = '<p>No active games found.</p>';
                }
            } else {
                displayMyGames(); // Refresh the list if specific item not found
            }
        } else {
            throw new Error(result.data?.message || "Delete request failed unexpectedly.");
        }
    } catch (error) {
        console.error("handleDeleteGame: Error deleting game:", error);
        alert(`Failed to delete game: ${error.message || "Unknown error"}`);
        if (button) button.disabled = false;
    }
}

/**
 * Formats the game status and opponent info for display.
 * @param {object} game - Game object from the backend.
 * @param {string} currentUserId - The ID of the currently logged-in user.
 * @returns {string} HTML string describing the game status.
 * MODIFIED: Returns an object with opponentDisplay and simplified status info
 * @returns {{opponentDisplay: string, isFinished: boolean}}
 * RESTORED: Returns more detailed info for active games display
 * @returns {{opponentDisplay: string, turnStatusText: string, isMyTurn: boolean, isFinished: boolean}}
 */
function formatGameInfo(game, currentUserId) {
    let opponentDisplay = game.opponentDisplayName || '(Unknown Opponent)';
    let turnStatusText = '';
    let isMyTurn = false;
    let isFinished = false;

    if (game.status === 'waiting') {
        if (game.opponentDisplayName && game.opponentDisplayName !== '(Waiting for opponent)') {
            opponentDisplay = game.opponentDisplayName;
            turnStatusText = `Waiting for opponent`;
        } else {
            opponentDisplay = '(Open Game)'; 
            turnStatusText = `Waiting for opponent`; 
        }
    } else if (game.status === 'finished') {
        isFinished = true;
        if (game.winnerId === currentUserId) {
            turnStatusText = `You Won!`;
        } else if (game.winnerId) { 
            turnStatusText = `You Lost`;
        } else {
            turnStatusText = `Game Over`; 
        }
    } else if (game.status === 'active') {
        if (game.isMyTurn) {
            isMyTurn = true;
            turnStatusText = `Your Turn!`;
        } else {
            turnStatusText = `${game.opponentDisplayName || 'Opponent'}'s Turn`;
        }
    }
    return {
        opponentDisplay,
        turnStatusText,
        isMyTurn,
        isFinished
    };
}

/**
 * Fetches the user's games via Cloud Function and updates the HTML.
 * @param {number} [limit=25] - Max games to fetch.
 */
export async function displayMyGames(limit = 25) {
  const activeGamesListDiv = document.getElementById('active-games-list');
  const gamesSection = document.getElementById('games-section');

  if (!activeGamesListDiv || !gamesSection) { // MODIFIED: Removed availableGamesListDiv check
    console.error("displayMyGames: Could not find required elements for active games list.");
    const gamesHeader = document.querySelector('#games-section .games-header');
    if (gamesHeader) gamesHeader.insertAdjacentHTML('afterend', '<p style="color: red;">Error loading game list.</p>');
    return;
  }

  const auth = getAuth();
  const currentUser = auth.currentUser;
  if (!currentUser) {
       activeGamesListDiv.innerHTML = '<p>Please log in to see your active games.</p>';
       // REMOVED: availableGamesListDiv.innerHTML = '<p>Please log in to see available games.</p>';
       return;
  }
  const currentUserId = currentUser.uid;

  if (!getMyGamesFunction) {
      try {
          const functions = getFunctions();
          getMyGamesFunction = httpsCallable(functions, 'getMyGamesCallable');
      } catch (error) {
          console.error("displayMyGames: Error getting Firebase Functions instance:", error);
          activeGamesListDiv.innerHTML = '<p>Error initializing game list service.</p>';
          // REMOVED: availableGamesListDiv.innerHTML = '';
          return;
      }
  }

  activeGamesListDiv.innerHTML = '<p>Loading active games...</p>';
  // REMOVED: availableGamesListDiv.innerHTML = '<p>Loading available games...</p>';

  try {
    console.log(`displayMyGames: Calling getMyGamesCallable...`);
    const result = await getMyGamesFunction({ /* Add limits if needed */ });
    const myGamesData = result.data?.myGames; // Only interested in myGames now
    // const availableGamesData = result.data?.availableGames; // REMOVED
    console.log("displayMyGames: Received data:", { myGamesData }); // MODIFIED Log

    if (!myGamesData) { // MODIFIED: Simplified check
        throw new Error("My games data not received from function call.");
    }

    let activeGamesHtml = '';
    let activeCount = 0;

    if (myGamesData.length > 0) {
      activeGamesHtml += '<ul class="my-games-list active">';
      myGamesData.forEach(game => {
        const gameInfo = formatGameInfo(game, currentUserId);
        const buttonText = 'Play'; 
        const isOwner = game.player1Id === currentUserId;
        let gameItemHtml = `
          <li class="${gameInfo.isMyTurn ? 'my-turn' : ''}"> 
            <div class="game-info">
               <div class="game-opponent">vs ${gameInfo.opponentDisplay}</div> 
               <div class="game-turn-status">${gameInfo.turnStatusText}</div>
            </div>
            <div class="game-actions ${!isOwner ? 'single-action' : ''}"> 
              <button class="open-game-btn" data-game-id="${game.gameId}">${buttonText}</button>
              ${isOwner ? `<button class="delete-game-btn" data-game-id="${game.gameId}">Delete</button>` : ''}
            </div>
          </li>
        `;
        if (game.status !== 'finished') { // Simplified to show non-finished games as active
            activeGamesHtml += gameItemHtml;
            activeCount++;
        }
      });
      activeGamesHtml += '</ul>';
    }

    activeGamesListDiv.innerHTML = activeCount > 0 ? activeGamesHtml : '<p>No active games found.</p>';
    
    // --- REMOVED: Population of Available Games List Div --- 
    
    gamesSection.removeEventListener('click', handleGameButtonClick);
    gamesSection.addEventListener('click', handleGameButtonClick);

    console.log("displayMyGames: Active games list displayed successfully."); // MODIFIED Log

  } catch (error) {
    console.error("displayMyGames: Error fetching or displaying active games:", error); // MODIFIED Log
    activeGamesListDiv.innerHTML = '<p>Error loading active games.</p>';
    // REMOVED: availableGamesListDiv.innerHTML = '<p>Error loading available games.</p>';
  }
}

function handleGameButtonClick(event) {
    if (event.target.classList.contains('open-game-btn')) {
        const gameId = event.target.dataset.gameId;
        handleOpenGame(gameId);
    }
    // else if (event.target.classList.contains('join-game-btn')) { // REMOVED - Join buttons are not handled here
    //     const gameId = event.target.dataset.gameId;
    //     handleJoinGame(gameId);
    // }
    else if (event.target.classList.contains('delete-game-btn')) {
        const gameId = event.target.dataset.gameId;
        handleDeleteGame(gameId);
    }
} 